import type { IconName } from '@/components/Icon/iconMap';
import type { SupportedCountry } from '@/constants/country';
import type {
  AddressValue,
  BankAccountValue,
  CommuteRouteListItem,
  ContractValue,
  DateRangeValue,
  DepartmentListItem,
  EmailListItemValue,
  EmergencyContactListItem,
  EmployeeSubIdListItem,
  ExtensionNumberListItem,
  FamilyPersonalInfoListItem,
  FileListItem,
  FileListValue,
  FileValue,
  FolderLinkValue,
  GradeValue,
  HometownValue,
  ImageType,
  JobCompetencieValue,
  JobFunctionValue,
  JobGradeValue,
  JobLevelValue,
  JobTitleValue,
  JobTypeValue,
  LeaveOfAbsenceListItem,
  NumberWithUnitValue,
  PersonNameValue,
  PhoneListItemValue,
  PhoneNumberValue,
  PhoneType,
  PlaceListItem,
  SnsAccountListItem,
  UrlValue,
  ValueType,
  WorkLocationValue,
  WorkModeValue,
} from '@/features/section-attribute/types';

import type { NonDeprecatedBadgeVariant } from '../CFormItems/impl/CFormBadgeItem';
import { JAPAN_DIAL_CODE } from '../CFormItems/impl/CFormPhoneItem';

/**
 * T のキーがすべて ValidKeys に含まれることをコンパイル時に強制するヘルパー。
 * ValidKeys に存在しないキーを追加すると、そのキーの値型が never に制約され
 * 型エラーになる。
 */
type StrictKeys<
  T extends { [K in keyof T]: K extends ValidKeys ? T[K] : never },
  ValidKeys extends string,
> = T;

/** K が keyof T のサブセットであることをコンパイル時に強制するヘルパー */
type EnsureKeysOf<T, K extends keyof T> = K;

/**
 * ValueType → coerce 後の型マッピング
 *
 * 各 ValueType がどの型に coerce されるかを定義する。
 * AttributeRenderer の switch 内で `as` キャストを排除し、
 * 型安全にデータを取り扱うための基盤。
 *
 * ここに含まれない ValueType（PROVISIONAL 等の特殊型）は
 * AttributeRenderer 側で個別にハンドリングする。
 *
 * キーは SchemaValueType（API スキーマの valueType union）のサブセットに
 * 制約されており、スキーマに存在しないキーを追加するとコンパイルエラーになる。
 */
/** Badge に icon フィールドを追加した UI 拡張型（section-attribute/types には持ち込まない） */
type BadgeValueWithIcon = {
  text: string;
  variant: NonDeprecatedBadgeVariant;
  icon?: IconName;
};

export type ValueTypeMap = StrictKeys<
  {
    // --- Primitive ---
    TEXT: string;
    TEXT_AREA: string;
    MULTI_TEXT: string[];
    NUMBER: number;
    BYTE_SIZE: number;
    BOOLEAN: boolean;

    // --- Date ---
    DATE: Date;
    BIRTHDAY: Date;
    DATE_TIME: Date;
    DATE_RANGE: DateRangeValue;

    // --- Select ---
    SELECT: string;
    MULTI_SELECT: string[];
    PERSON_SELECT: string;
    DEPARTMENT_SELECT: string;
    EMPLOYMENT_TYPE_SELECT: string;
    CONTRACTOR_TYPE_SELECT: string;
    PLACE_SELECT: string;
    PLACE_MULTI_SELECT: string[];

    // --- String-ish ---
    POSTAL_CODE: string;
    EMAIL_ADDRESS: string;
    COUNTRY: SupportedCountry;
    MULTI_COUNTRIES: string[];
    STATE_PROVINCE: string;

    // --- Complex objects ---
    ADDRESS: AddressValue;
    PHONE_NUMBER: PhoneNumberValue;
    PERSON_NAME: PersonNameValue;
    WORK_LOCATION: WorkLocationValue;
    CONTRACT: ContractValue;
    BANK_ACCOUNT: BankAccountValue;
    URL: UrlValue;
    BADGE: BadgeValueWithIcon;
    FOLDER_LINK: FolderLinkValue;
    FILE: FileValue[];
    FILE_LIST: FileListValue;
    IMAGE: {
      id: string;
      type: ImageType | null;
      imageUrl?: string;
      isGenerated: boolean;
    };
    NUMBER_WITH_UNIT: NumberWithUnitValue;

    // --- Master data ---
    GRADE: GradeValue;
    JOB_TITLE: JobTitleValue;
    JOB_TYPE_LIST: JobTypeValue[];
    JOB_FUNCTION_LIST: JobFunctionValue[];
    JOB_LEVEL: JobLevelValue;
    JOB_COMPETENCIE: JobCompetencieValue;
    JOB_GRADE: JobGradeValue;
    WORK_MODE: WorkModeValue;

    // --- List types ---
    PHONE_NUMBER_LIST: PhoneListItemValue[];
    EMAIL_ADDRESS_LIST: EmailListItemValue[];
    EXTENSION_NUMBER_LIST: ExtensionNumberListItem[];
    EMPLOYEE_SUB_ID_LIST: EmployeeSubIdListItem[];
    DEPARTMENT_LIST: DepartmentListItem[];
    SNS_ACCOUNT_LIST: SnsAccountListItem[];
    EMERGENCY_CONTACT_LIST: EmergencyContactListItem[];
    FAMILY_PERSONAL_INFO_LIST: FamilyPersonalInfoListItem[];
    COMMUTE_ROUTE_LIST: CommuteRouteListItem[];
    HOMETOWN_LIST: HometownValue[];
    LEAVE_OF_ABSENCE_LIST: LeaveOfAbsenceListItem[];
    PLACE_MULTI_SELECT_WITH_PRIMARY: PlaceListItem[];
  },
  ValueType
>;

/**
 * パーサーが null/undefined 入力時にもデフォルト値を返す ValueType。
 * これらの型は CoercedValue から `| undefined` が除外される。
 * EnsureKeysOf により、ValueTypeMap に存在しないキーを追加するとコンパイルエラーになる。
 */
type DefaultedKeys = EnsureKeysOf<
  ValueTypeMap,
  | 'DATE_RANGE'
  | 'ADDRESS'
  | 'PHONE_NUMBER'
  | 'PERSON_NAME'
  | 'WORK_LOCATION'
  | 'CONTRACT'
  | 'BANK_ACCOUNT'
  | 'GRADE'
  | 'JOB_TITLE'
  | 'JOB_TYPE_LIST'
  | 'JOB_FUNCTION_LIST'
  | 'JOB_LEVEL'
  | 'JOB_COMPETENCIE'
  | 'JOB_GRADE'
  | 'WORK_MODE'
  | 'PHONE_NUMBER_LIST'
  | 'EMAIL_ADDRESS_LIST'
  | 'EXTENSION_NUMBER_LIST'
  | 'EMPLOYEE_SUB_ID_LIST'
  | 'DEPARTMENT_LIST'
  | 'SNS_ACCOUNT_LIST'
  | 'EMERGENCY_CONTACT_LIST'
  | 'FAMILY_PERSONAL_INFO_LIST'
  | 'COMMUTE_ROUTE_LIST'
  | 'HOMETOWN_LIST'
  | 'LEAVE_OF_ABSENCE_LIST'
  | 'PLACE_MULTI_SELECT_WITH_PRIMARY'
>;

/**
 * ValueType に応じた coerce 後の型を取得する。
 * DefaultedKeys に含まれる型はデフォルト値が保証されるため undefined を含まない。
 * マップに存在しない ValueType は unknown にフォールバック。
 */
export type CoercedValue<T extends string> = T extends DefaultedKeys
  ? T extends keyof ValueTypeMap
    ? ValueTypeMap[T]
    : unknown
  : T extends keyof ValueTypeMap
    ? ValueTypeMap[T] | undefined
    : unknown;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type ValueParser<T> = (value: unknown) => T | undefined;

function parseDate(v: unknown): Date | undefined {
  if (v instanceof Date) return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function asRawObject(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function asString(v: unknown): string | undefined {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

const valueParsers: {
  [K in keyof ValueTypeMap]?: ValueParser<ValueTypeMap[K]>;
} = {
  // --- Primitive ---
  TEXT: (v) =>
    asString(v) ??
    (typeof v === 'object' && v !== null
      ? JSON.stringify(v, null, 2)
      : undefined),
  TEXT_AREA: (v) =>
    asString(v) ??
    (typeof v === 'object' && v !== null
      ? JSON.stringify(v, null, 2)
      : undefined),
  MULTI_TEXT: (v) => (Array.isArray(v) ? (v as string[]) : undefined),
  NUMBER: asNumber,
  BYTE_SIZE: asNumber,
  BOOLEAN: (v) => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v.toLowerCase() === 'true' || v === '1';
    return undefined;
  },

  // --- Date ---
  DATE: parseDate,
  BIRTHDAY: parseDate,
  DATE_TIME: parseDate,
  DATE_RANGE: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { startDate: null, endDate: null };
    return {
      startDate:
        (raw.startDate as ValueTypeMap['DATE_RANGE']['startDate']) ?? null,
      endDate: (raw.endDate as ValueTypeMap['DATE_RANGE']['endDate']) ?? null,
    };
  },

  // --- Select ---
  SELECT: (v) => asString(v),
  MULTI_SELECT: (v) => (Array.isArray(v) ? (v as string[]) : undefined),
  PERSON_SELECT: (v) => asString(v),
  DEPARTMENT_SELECT: (v) => asString(v),
  EMPLOYMENT_TYPE_SELECT: (v) => asString(v),
  CONTRACTOR_TYPE_SELECT: (v) => asString(v),
  PLACE_SELECT: (v) => asString(v),
  PLACE_MULTI_SELECT: (v) => (Array.isArray(v) ? (v as string[]) : undefined),

  // --- String-ish ---
  POSTAL_CODE: (v) => asString(v),
  EMAIL_ADDRESS: (v) => asString(v),
  COUNTRY: (v) => asString(v) as SupportedCountry | undefined,
  MULTI_COUNTRIES: (v) => (Array.isArray(v) ? (v as string[]) : undefined),
  STATE_PROVINCE: (v) => asString(v),

  // --- Master data ---
  GRADE: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { gradeId: '', name: '' };
    return { gradeId: String(raw.gradeId ?? ''), name: String(raw.name ?? '') };
  },
  JOB_TITLE: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { jobTitleId: '', name: '' };
    return {
      jobTitleId: String(raw.jobTitleId ?? ''),
      name: String(raw.name ?? ''),
    };
  },
  JOB_TYPE_LIST: (v) => (Array.isArray(v) ? (v as JobTypeValue[]) : []),
  JOB_FUNCTION_LIST: (v) => (Array.isArray(v) ? (v as JobFunctionValue[]) : []),
  JOB_LEVEL: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { jobLevelId: '', name: '' };
    return {
      jobLevelId: String(raw.jobLevelId ?? ''),
      name: String(raw.name ?? ''),
    };
  },
  JOB_COMPETENCIE: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { jobCompetencyId: '', name: '' };
    return {
      jobCompetencyId: String(raw.jobCompetencyId ?? ''),
      name: String(raw.name ?? ''),
    };
  },
  JOB_GRADE: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { jobGradeId: '', name: '' };
    return {
      jobGradeId: String(raw.jobGradeId ?? ''),
      name: String(raw.name ?? ''),
    };
  },
  WORK_MODE: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { workModeId: '', name: '' };
    return {
      workModeId: String(raw.workModeId ?? ''),
      name: String(raw.name ?? ''),
    };
  },

  // --- Complex objects ---
  ADDRESS: (v) => {
    const raw = asRawObject(v);
    if (!raw)
      return {
        id: null,
        residenceType: null,
        countryCode: null,
        countryName: null,
        postalCode: '',
        stateProvinceCode: '',
        stateProvinceName: '',
        city: '',
        streetAddress: '',
        addressLine2: '',
      };
    return {
      id: (raw.id as string | null) ?? null,
      residenceType: (raw.residenceType as string | null) ?? null,
      countryCode: String(raw.countryCode ?? ''),
      countryName: (raw.countryName as string | null) ?? null,
      postalCode: String(raw.postalCode ?? ''),
      stateProvinceCode: String(raw.stateProvinceCode ?? ''),
      stateProvinceName: String(raw.stateProvinceName ?? ''),
      city: String(raw.city ?? ''),
      streetAddress: String(raw.streetAddress ?? ''),
      addressLine2: String(raw.addressLine2 ?? ''),
    };
  },
  PHONE_NUMBER: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { dialCode: JAPAN_DIAL_CODE, phoneNumber: '' };
    return {
      dialCode: String(raw.dialCode ?? JAPAN_DIAL_CODE),
      phoneNumber: String(raw.phoneNumber ?? ''),
      phoneType:
        raw.phoneType != null
          ? (String(raw.phoneType) as PhoneType)
          : undefined,
    };
  },
  PERSON_NAME: (v) => {
    const raw = asRawObject(v);
    if (!raw)
      return {
        familyName: '',
        givenName: '',
        middleName: '',
        familyNameKana: '',
        givenNameKana: '',
        middleNameKana: '',
        familyNameEn: '',
        givenNameEn: '',
        middleNameEn: '',
        previousFamilyName: '',
        previousFamilyNameKana: '',
        previousFamilyNameEn: '',
      };
    return {
      familyName: String(raw.familyName ?? ''),
      givenName: String(raw.givenName ?? ''),
      middleName: String(raw.middleName ?? ''),
      familyNameKana: String(raw.familyNameKana ?? ''),
      givenNameKana: String(raw.givenNameKana ?? ''),
      middleNameKana: String(raw.middleNameKana ?? ''),
      familyNameEn: String(raw.familyNameEn ?? ''),
      givenNameEn: String(raw.givenNameEn ?? ''),
      middleNameEn: String(raw.middleNameEn ?? ''),
      previousFamilyName: String(raw.previousFamilyName ?? ''),
      previousFamilyNameKana: String(raw.previousFamilyNameKana ?? ''),
      previousFamilyNameEn: String(raw.previousFamilyNameEn ?? ''),
    };
  },
  WORK_LOCATION: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { workLocationId: '', name: '', details: null };
    return {
      workLocationId: String(raw.workLocationId ?? ''),
      name: String(raw.name ?? ''),
      details: raw.details ? String(raw.details) : null,
    };
  },
  CONTRACT: (v) => {
    const raw = asRawObject(v);
    if (!raw) return { contractId: '', name: '', contractAggregateId: '' };
    return {
      contractId: String(raw.contractId ?? ''),
      name: String(raw.name ?? ''),
      contractAggregateId: String(raw.contractAggregateId ?? ''),
    };
  },
  BANK_ACCOUNT: (v) => {
    const raw = asRawObject(v);
    if (!raw)
      return {
        bankName: '',
        bankCode: '',
        branchName: '',
        branchCode: '',
        accountType: 'ORDINARY' as BankAccountValue['accountType'],
        accountNumber: '',
        accountHolderName: '',
      };
    return {
      bankName: String(raw.bankName ?? ''),
      bankCode: String(raw.bankCode ?? ''),
      branchName: String(raw.branchName ?? ''),
      branchCode: String(raw.branchCode ?? ''),
      accountType:
        (raw.accountType as BankAccountValue['accountType']) ?? 'ORDINARY',
      accountNumber: String(raw.accountNumber ?? ''),
      accountHolderName: String(raw.accountHolderName ?? ''),
    };
  },
  URL: (v) => {
    const raw = asRawObject(v);
    if (!raw || !('url' in raw)) return undefined;
    return {
      url: String(raw.url ?? ''),
      displayText: raw.displayText ? String(raw.displayText) : null,
    };
  },
  BADGE: (v) => {
    const raw = asRawObject(v);
    if (!raw || !('text' in raw) || !('variant' in raw)) return undefined;
    return {
      text: String(raw.text ?? ''),
      variant: String(raw.variant ?? '') as NonDeprecatedBadgeVariant,
      icon: raw.icon ? (String(raw.icon) as IconName) : undefined,
    };
  },
  FOLDER_LINK: (v) => {
    const raw = asRawObject(v);
    if (!raw || !('folderId' in raw) || !('folderName' in raw))
      return undefined;
    return {
      folderId: String(raw.folderId ?? ''),
      folderName: String(raw.folderName ?? ''),
      linkTo: String(raw.linkTo ?? ''),
    };
  },
  FILE: (v) => {
    const files = asArray(v);
    return files.map((file) => {
      return file as FileValue;
    });
  },
  FILE_LIST: (v) => {
    const raw = asRawObject(v);
    if (!raw || !('files' in raw) || !('totalCount' in raw)) return undefined;
    return {
      files: Array.isArray(raw.files) ? (raw.files as FileListItem[]) : [],
      totalCount: Number(raw.totalCount ?? 0),
    };
  },
  IMAGE: (v) => {
    const raw = asRawObject(v);
    if (!raw || !('id' in raw) || !('isGenerated' in raw)) return undefined;
    return {
      id: String(raw.id ?? ''),
      type: (raw.imageType as ImageType | null) ?? null,
      ...(raw.imageUrl != null ? { imageUrl: String(raw.imageUrl) } : {}),
      isGenerated: Boolean(raw.isGenerated),
    };
  },
  NUMBER_WITH_UNIT: (v) => {
    const raw = asRawObject(v);
    if (!raw || !('value' in raw || 'unitCode' in raw)) return undefined;
    return {
      value: raw.value != null ? Number(raw.value) : undefined,
      unitCode: raw.unitCode ? String(raw.unitCode) : undefined,
    };
  },

  // --- List types ---
  PHONE_NUMBER_LIST: (v) => {
    const items = asArray(v);
    return items.map((item) => {
      const raw = asRawObject(item) ?? {};
      return {
        dialCode: String(raw.dialCode ?? ''),
        phoneNumber: String(raw.phoneNumber ?? ''),
        phoneType: raw.phoneType != null ? String(raw.phoneType) : undefined,
      } as PhoneListItemValue;
    });
  },
  EMAIL_ADDRESS_LIST: (v) => {
    const items = asArray(v);
    return items.map((item) => {
      if (typeof item === 'string') {
        return { email: item, isPrimary: false };
      }
      const raw = asRawObject(item) ?? {};
      return {
        email: String(raw.email ?? ''),
        isPrimary: Boolean(raw.isPrimary),
      };
    });
  },
  EXTENSION_NUMBER_LIST: (v) =>
    Array.isArray(v) ? (v as ExtensionNumberListItem[]) : [],
  EMPLOYEE_SUB_ID_LIST: (v) => {
    const items = asArray(v);
    return items.map((item) => {
      const raw = asRawObject(item) ?? {};
      return {
        employeeSubIdNameId: String(
          raw.employeeSubIdNameId ?? raw.nameId ?? '',
        ),
        employeeSubIdName: String(raw.employeeSubIdName ?? raw.name ?? ''),
        employeeSubId: String(raw.employeeSubId ?? ''),
      } satisfies EmployeeSubIdListItem;
    });
  },
  DEPARTMENT_LIST: (v) => (Array.isArray(v) ? (v as DepartmentListItem[]) : []),
  SNS_ACCOUNT_LIST: (v) =>
    Array.isArray(v) ? (v as SnsAccountListItem[]) : [],
  EMERGENCY_CONTACT_LIST: (v) =>
    Array.isArray(v) ? (v as EmergencyContactListItem[]) : [],
  FAMILY_PERSONAL_INFO_LIST: (v) =>
    Array.isArray(v) ? (v as FamilyPersonalInfoListItem[]) : [],
  COMMUTE_ROUTE_LIST: (v) =>
    Array.isArray(v) ? (v as CommuteRouteListItem[]) : [],
  HOMETOWN_LIST: (v) => (Array.isArray(v) ? (v as HometownValue[]) : []),
  LEAVE_OF_ABSENCE_LIST: (v) =>
    Array.isArray(v) ? (v as LeaveOfAbsenceListItem[]) : [],
  PLACE_MULTI_SELECT_WITH_PRIMARY: (v) =>
    Array.isArray(v) ? (v as PlaceListItem[]) : [],
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * APIレスポンスの属性値を valueType に基づいてフロントエンドの適切な型に変換する。
 *
 * APIレスポンスの value フィールドが不定形な型（Record<string, never> 等）で
 * 返される場合に、valueType に応じた型安全な変換を行う汎用ユーティリティ。
 *
 * ValueTypeMap に登録されたパーサーがある場合はそれを使用し、
 * ない場合はプリミティブ型のフォールバック変換を行う。
 *
 * @example
 * ```ts
 * // 戻り値は CoercedValue<"ADDRESS"> = Address 型に推論される
 * const address = coerceAttributeValue(attr.value, "ADDRESS");
 * ```
 */
export function coerceAttributeValue<T extends string>(
  value: unknown,
  valueType: T,
): CoercedValue<T> {
  const parser = valueParsers[valueType as keyof typeof valueParsers];
  if (parser) {
    return parser(value) as CoercedValue<T>;
  }

  if (value === null || value === undefined) {
    return undefined as CoercedValue<T>;
  }

  // フォールバック: プリミティブは string に変換、オブジェクトは undefined
  if (typeof value === 'string') return value as CoercedValue<T>;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value) as CoercedValue<T>;
  }
  return undefined as CoercedValue<T>;
}
